import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  BusinessManagementDocument,
  BusinessesDocument,
  MeDocument,
  VerifyEmailAndAcceptInviteMutation,
  useBusinessesQuery,
  useVerifyEmailAndAcceptInviteMutation,
} from "../../generated/graphql";
import BusinessPage from "./BusinessPage";
import getErrorMessage from "../../lib/query/getErrorMessage";
import Card from "../../components/Card";
import Label from "../../components/Label";
import VerifyAndInviteEmailButton from "./VerifyAndInviteEmailButton";
import Button from "../../components/buttons/Button";

export default function EmailLinkPage(): React.ReactElement | null {
  const params = useParams();

  const navigate = useNavigate();

  const { businessInviteToken, verifyEmailAndInviteToken } = params;

  const {
    data: businessesQueryData,
    error: businessesQueryError,
    loading: businessesQueryLoading,
  } = useBusinessesQuery();

  const [
    verifyEmailAndAcceptInvite,
    {
      data: verifyEmailAndAcceptInviteData,
      error: verifyEmailAndAcceptInviteError,
      loading: verifyEmailAndAcceptInviteLoading,
      called: verifyEmailAndAcceptInviteCalled,
    },
  ] = useVerifyEmailAndAcceptInviteMutation({
    refetchQueries: [
      BusinessesDocument,
      BusinessManagementDocument,
      MeDocument,
    ],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    if (verifyEmailAndAcceptInviteCalled) {
      return;
    }

    if (!businessInviteToken && !verifyEmailAndInviteToken) {
      return;
    }

    if (!businessesQueryData) {
      return;
    }

    verifyEmailAndAcceptInvite({
      variables: { input: { businessInviteToken, verifyEmailAndInviteToken } },
    }).catch((error) => {
      console.error("[verifyEmailAndAcceptInvite] error", error);
    });
  }, [
    verifyEmailAndAcceptInviteCalled,
    businessInviteToken,
    verifyEmailAndInviteToken,
    businessesQueryData,
    verifyEmailAndAcceptInvite,
  ]);

  const error = businessesQueryError || verifyEmailAndAcceptInviteError;

  return (
    <BusinessPage>
      <Card layout="horizontal">
        <div className="flex w-full flex-col gap-[12px]">
          {businessesQueryLoading || verifyEmailAndAcceptInviteLoading ? (
            <Label type="title3">Loading...</Label>
          ) : error ? (
            <>
              <Label type="title3">Error: {getErrorMessage(error)}</Label>
              <VerifyAndInviteEmailButton text="Send new link" />
            </>
          ) : verifyEmailAndAcceptInviteData ? (
            <>
              <div>
                <Label type="title1">Success</Label>
                <p className="mt-1 text-tx-muted">
                  {getMessage(verifyEmailAndAcceptInviteData)}
                </p>
              </div>
              <Button
                text="View projects"
                onClick={() => {
                  if (
                    businessesQueryData?.businesses.length !==
                    verifyEmailAndAcceptInviteData.verifyEmailAndAcceptInvite
                      .totalBusinessesCount
                  ) {
                    // This is a fix to force a page reload in case the refetch
                    // failed or didn't get the latest data.
                    // TODO: Find a better way to do this.
                    console.error("[verifyEmailAndAcceptInvite] refetch error");
                    window.location.href = "/";
                    return Promise.resolve();
                  }
                  return navigate("/");
                }}
                intent="primary"
                weight="outlined"
              />
            </>
          ) : (
            <Label type="title3">Unexpected error. Please try again.</Label>
          )}
        </div>
      </Card>
    </BusinessPage>
  );
}

function getMessage(data: VerifyEmailAndAcceptInviteMutation): string {
  const { verifiedEmail, totalBusinessesCount } =
    data.verifyEmailAndAcceptInvite;

  if (totalBusinessesCount > 1) {
    const teamCount = totalBusinessesCount - 1;

    const prefix = verifiedEmail ? "We've verified your email and you" : "You";

    return `${prefix}'re now a member of ${teamCount} ${teamCount === 1 ? "team" : "teams"}.`;
  }

  if (totalBusinessesCount === 1 && verifiedEmail) {
    return "We've verified your email.";
  }

  return "";
}
